import React, { useState, useEffect } from 'react';
import { pageTabsSx, pagePaperSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface Installation {
  installation_id: string;
  request_id: string;
  installation_status: 'pending' | 'completed' | 'rejected';
  installation_notes?: string;
  completion_notes?: string;
  assigned_at: string;
  completed_at?: string;
  rejected_at?: string;
  whole_good_number: string;
  request_status: string;
  asset_name?: string;
  serial_number?: string;
  assigned_to_first_name: string;
  assigned_to_last_name: string;
  assigned_to_email: string;
  assigned_by_first_name: string;
  assigned_by_last_name: string;
}

type StatusFilter = 'all' | 'pending' | 'completed' | 'rejected';

const Installations: React.FC = () => {
  const [allInstallations, setAllInstallations] = useState<Installation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedInstallation, setSelectedInstallation] = useState<Installation | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const fetchInstallations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Always fetch all installations, filter client-side
      const response = await axios.get(
        `${API_BASE_URL}/outgoing-deliveries/installations?status=all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setAllInstallations(response.data.installations || []);
      } else {
        throw new Error(response.data.message || 'Failed to fetch installations');
      }
    } catch (err: any) {
      console.error('Error fetching installations:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch installations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstallations();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: StatusFilter) => {
    setStatusFilter(newValue);
  };

  // Filter installations based on current tab
  const filteredInstallations = statusFilter === 'all' 
    ? allInstallations 
    : allInstallations.filter(i => i.installation_status === statusFilter);

  const handleViewDetails = (installation: Installation) => {
    setSelectedInstallation(installation);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedInstallation(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'completed':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'rejected':
        return <CancelIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFilteredCount = (status: StatusFilter) => {
    if (status === 'all') return allInstallations.length;
    return allInstallations.filter(i => i.installation_status === status).length;
  };

  if (loading && allInstallations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader
        title="Installations"
        subtitle="Operations"
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchInstallations()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ ...pagePaperSx, mb: 3 }}>
        <Tabs
          value={statusFilter}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={pageTabsSx}
        >
          <Tab label={`All (${getFilteredCount('all')})`} value="all" />
          <Tab label={`Pending (${getFilteredCount('pending')})`} value="pending" />
          <Tab label={`Completed (${getFilteredCount('completed')})`} value="completed" />
          <Tab label={`Rejected (${getFilteredCount('rejected')})`} value="rejected" />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Wholegood Number</TableCell>
              <TableCell>Asset</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Assigned By</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned Date</TableCell>
              <TableCell>Completed/Rejected Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInstallations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No installations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredInstallations.map((installation) => (
                <TableRow key={installation.installation_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {installation.whole_good_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {installation.asset_name ? (
                      <>
                        <Typography variant="body2">{installation.asset_name}</Typography>
                        {installation.serial_number && (
                          <Typography variant="caption" color="text.secondary">
                            SN: {installation.serial_number}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No asset linked
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {installation.assigned_to_first_name} {installation.assigned_to_last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {installation.assigned_to_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {installation.assigned_by_first_name} {installation.assigned_by_last_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={installation.installation_status.toUpperCase()}
                      color={getStatusColor(installation.installation_status)}
                      size="small"
                      icon={getStatusIcon(installation.installation_status) || undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(installation.assigned_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(installation.completed_at || installation.rejected_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(installation)}
                        color="primary"
                      >
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Installation Details</DialogTitle>
        <DialogContent dividers>
          {selectedInstallation && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Wholegood Number
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedInstallation.whole_good_number}
                </Typography>
              </Box>

              {selectedInstallation.asset_name && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Asset
                  </Typography>
                  <Typography variant="body1">
                    {selectedInstallation.asset_name}
                  </Typography>
                  {selectedInstallation.serial_number && (
                    <Typography variant="caption" color="text.secondary">
                      Serial Number: {selectedInstallation.serial_number}
                    </Typography>
                  )}
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedInstallation.installation_status.toUpperCase()}
                  color={getStatusColor(selectedInstallation.installation_status)}
                  size="small"
                  icon={getStatusIcon(selectedInstallation.installation_status) || undefined}
                  sx={{ mt: 0.5 }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Assigned To
                </Typography>
                <Typography variant="body1">
                  {selectedInstallation.assigned_to_first_name} {selectedInstallation.assigned_to_last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedInstallation.assigned_to_email}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Assigned By
                </Typography>
                <Typography variant="body1">
                  {selectedInstallation.assigned_by_first_name} {selectedInstallation.assigned_by_last_name}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Assigned Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(selectedInstallation.assigned_at)}
                </Typography>
              </Box>

              {(selectedInstallation.completed_at || selectedInstallation.rejected_at) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {selectedInstallation.installation_status === 'completed' ? 'Completed' : 'Rejected'} Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedInstallation.completed_at || selectedInstallation.rejected_at)}
                  </Typography>
                </Box>
              )}

              {selectedInstallation.installation_notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Installation Notes
                  </Typography>
                  <Typography variant="body2">
                    {selectedInstallation.installation_notes}
                  </Typography>
                </Box>
              )}

              {selectedInstallation.completion_notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Completion Notes
                  </Typography>
                  <Typography variant="body2">
                    {selectedInstallation.completion_notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Installations;

